import { useState, useCallback, useMemo } from "react";
import PageShell from "../../../components/PageShell";
import DataTable from "../../../components/DataTable";
import DataForm from "../../../components/DataForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import ConfirmDialog from "../../../components/ConfirmDialog";
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from "../../../components/ui/context-menu";
import {
  useServices,
  useSaveService,
  useArchiveService,
} from "../hooks/useServices";
import { useServiceNames } from "../hooks/useServiceNames";
import { useLocationOptions } from "../hooks/useOptions";
import toast from "react-hot-toast";
import { useTranslation } from "../../../i18n";

const COMMON_COLUMNS = [
  { header: "col.serviceName", accessor: "service", type: "text" },
  { header: "col.seats", accessor: "seats", type: "number" },
  { header: "col.serviceActive", accessor: "active", type: "boolean" },
];

const CREATE_DEFAULTS = { service: "", location: "", seats: 1, active: true };

export default function ServicesPage() {
  const { t } = useTranslation();
  const { data: services, isLoading } = useServices();
  const { data: serviceNameOptions } = useServiceNames();
  const { data: locationOptions } = useLocationOptions();
  const saveMutation = useSaveService();
  const archiveMutation = useArchiveService();
  const [editingRow, setEditingRow] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [values, setValues] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const columns = useMemo(
    () => [
      ...COMMON_COLUMNS.slice(0, 1),
      {
        header: "col.location",
        accessor: (row) =>
          locationOptions?.find((l) => l.value === row.location)?.name ??
          row.location,
        type: "text",
      },
      ...COMMON_COLUMNS.slice(1),
    ],
    [locationOptions],
  );

  const openEdit = useCallback((row) => {
    setEditingRow(row);
    setValues({
      service: row.service,
      location: String(row.location),
      seats: row.seats,
      active: row.active,
    });
    setIsNew(false);
  }, []);

  const openCreate = useCallback(() => {
    setEditingRow(CREATE_DEFAULTS);
    setValues({ ...CREATE_DEFAULTS });
    setIsNew(true);
  }, []);

  const handleSave = () => {
    if (!values) return;
    saveMutation.mutate(values, {
      onSuccess: () => {
        toast.success(
          isNew ? t("toast.recordCreated") : t("toast.recordUpdated"),
        );
        setEditingRow(null);
        setValues(null);
      },
      onError: () => toast.error(t("toast.recordSaveFailed")),
    });
  };

  const handleArchive = () => {
    if (!archiveTarget) return;
    archiveMutation.mutate(
      { service: archiveTarget.service, location: archiveTarget.location },
      {
        onSuccess: () => {
          toast.success(t("toast.serviceArchived"));
          setArchiveTarget(null);
        },
        onError: () => toast.error(t("toast.serviceArchiveFailed")),
      },
    );
  };

  const renderArchiveMenu = useCallback(
    (row, children) => (
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            className="bg-c-red-500 hover:bg-c-red-600 text-white"
            onClick={() => setArchiveTarget(row)}
          >
            {t("common.archive")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    ),
    [],
  );

  const formOptions = useMemo(
    () => ({
      service: serviceNameOptions?.map((s) => ({ value: s, name: s })),
      location: locationOptions,
    }),
    [serviceNameOptions, locationOptions],
  );

  const FORM_FIELDS = useMemo(
    () => [
      {
        name: "service",
        label: "form.serviceName",
        type: "select",
        fetchOptions: () =>
          Promise.resolve(
            serviceNameOptions?.map((s) => ({ value: s, name: s })) || [],
          ),
      },
      {
        name: "location",
        label: "form.location",
        type: "select",
        fetchOptions: () => Promise.resolve(locationOptions || []),
      },
      { name: "seats", label: "form.seats", type: "number" },
      {
        name: "active",
        label: "form.active",
        type: "boolean",
        required: false,
      },
    ],
    [serviceNameOptions, locationOptions],
  );

  return (
    <PageShell
      title={t("page.services")}
      actions={<Button onClick={openCreate}>{t("common.add")}</Button>}
    >
      {isLoading ? (
        <p className="text-c-gray-400">{t("common.loading")}</p>
      ) : (
        <DataTable
          columns={columns}
          data={services}
          onRowClick={openEdit}
          renderContextMenu={renderArchiveMenu}
        />
      )}
      <Dialog
        open={!!editingRow}
        onOpenChange={(o) => {
          if (!o) {
            setEditingRow(null);
            setValues(null);
          }
        }}
      >
        <DialogContent className="left-auto right-0 top-0 translate-x-0 translate-y-0 h-dvh max-h-none rounded-none sm:rounded-none border-r-0 border-t-0 border-b-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right min-w-[480px] grid-rows-[auto_1fr_auto]">
          <DialogHeader>
            <DialogTitle>
              {isNew ? t("dialog.addService") : t("dialog.editRecord")}
            </DialogTitle>
          </DialogHeader>
          <form
            id="edit-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="flex-1 overflow-y-auto px-2"
          >
            <DataForm
              fields={FORM_FIELDS}
              values={values}
              onChange={setValues}
              options={formOptions}
            />
          </form>
          <DialogFooter className="flex gap-2">
            <Button type="submit" form="edit-form">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => {
          if (!o) setArchiveTarget(null);
        }}
        itemName={`${t("page.services").toLowerCase()} "${archiveTarget?.service}"`}
        onConfirm={handleArchive}
      />
    </PageShell>
  );
}
